// ─────────────────────────────────────────────────────────────────────────────
// Life Balance assessment — bidirectional ("just-right") scorer.
// Spec: docs/LIFE_BALANCE_ASSESSMENT_PLAN.md §5. The scale is 1–9 where 5 = Ideal;
// both too little (Deficient) and too much (Excessive) score badly. The engine's
// default "higher = better" averaging is wrong here, so this is a custom scorer.
// Pure functions only — unit-tested.
// ─────────────────────────────────────────────────────────────────────────────

// Display order matches the app's canonical domain order. Scoring is keyed by
// name, so order is display-only.
//
// ⚠️⚠️ THESE ARE STORAGE KEYS, NOT LABELS — do not "fix" them when a domain is
// renamed. Saved results use them as object keys (`dimension_scores`), and
// `assessment_questions.dimension` is seeded with them, so editing one here
// orphans every answer and every past result written under it — silently, since
// the domain would simply stop being found. `Ecological` is the live example:
// the domain is now DISPLAYED as "Material", and every surface renders it
// through `domainDisplayName()` while this key stays put.
export const LB_DOMAINS = [
  'Personal',
  'Social',
  'Occupational',
  'Avocational',
  'Ecological',
  'Intellectual',
  'Existential',
] as const;

export const LB_PARAMETERS = [
  'Effort',
  'Effectiveness',
  'Satisfaction',
  'Confidence',
  'Importance',
  'Clarity',
  'Maturity',
] as const;

export type LbDomain = (typeof LB_DOMAINS)[number];
export type LbParameter = (typeof LB_PARAMETERS)[number];
export type LbStatus = 'Deficient' | 'Balanced' | 'Excessive';
export type LbPotential = 'Low' | 'Mid' | 'High';

export const IDEAL = 5;

// Bidirectional status palette, shared by every Life Balance view: too little
// (amber) · just right (brand green) · too much (rose).
export const LB_STATUS_COLOR: Record<LbStatus, string> = {
  Deficient: '#f59e0b',
  Balanced: '#10b981',
  Excessive: '#f43f5e',
};

const clampAnswer = (a: number): number => Math.min(9, Math.max(1, Math.round(a)));
const round1 = (n: number) => Math.round(n * 10) / 10;

/** Signed distance from ideal: -4 (far too little) … +4 (far too much). */
export function deviation(answer: number): number {
  return clampAnswer(answer) - IDEAL;
}

/** 0–100, higher = more balanced. 5→100, 4/6→75, 3/7→50, 2/8→25, 1/9→0. */
export function balanceScore(answer: number): number {
  return Math.round(100 * (1 - Math.abs(clampAnswer(answer) - IDEAL) / 4));
}

/** Per-cell band (matches the Notion 4–5–6 green band). */
export function cellStatus(answer: number): LbStatus {
  const a = clampAnswer(answer);
  if (a <= 3) return 'Deficient';
  if (a >= 7) return 'Excessive';
  return 'Balanced';
}

/** Per-cell improvement headroom from |a−5|. */
export function cellPotential(answer: number): LbPotential {
  const d = Math.abs(clampAnswer(answer) - IDEAL);
  if (d <= 1) return 'Low';
  if (d === 2) return 'Mid';
  return 'High';
}

/** Aggregate status from a raw average (§5: <3.5 Deficient, >6.5 Excessive, else Balanced). */
export function aggregateStatus(rawAvg: number): LbStatus {
  if (rawAvg < 3.5) return 'Deficient';
  if (rawAvg > 6.5) return 'Excessive';
  return 'Balanced';
}

/**
 * Aggregate improvement headroom from the balance average. Bands chosen to
 * reproduce the §5.1 example payload (balance 38→High, 44/62/71→Mid):
 * <40 → High, 40–<80 → Mid, ≥80 → Low.
 */
export function aggregatePotential(balanceAvg: number): LbPotential {
  if (balanceAvg < 40) return 'High';
  if (balanceAvg < 80) return 'Mid';
  return 'Low';
}

const mean = (xs: number[]): number => (xs.length ? xs.reduce((s, n) => s + n, 0) / xs.length : 0);

export interface LbCellInput {
  domain: string;
  parameter: string;
  answer: number;
}

export interface LbCell {
  domain: string;
  parameter: string;
  answer: number;
  deviation: number;
  balance: number;
  status: LbStatus;
  potential: LbPotential;
}

export interface LbAggregate {
  rawAvg: number;
  balance: number;
  status: LbStatus;
  potential: LbPotential;
}

export interface LifeBalanceResult {
  overall: LbAggregate;
  domains: Record<string, LbAggregate>;
  parameters: Record<string, LbAggregate>;
  matrix: LbCell[];
}

function aggregate(answers: number[]): LbAggregate {
  const rawAvg = round1(mean(answers));
  const balance = Math.round(mean(answers.map(balanceScore)));
  return { rawAvg, balance, status: aggregateStatus(mean(answers)), potential: aggregatePotential(balance) };
}

/**
 * Build the full §5.1 `result_scores` payload from the answered cells. Accepts
 * any subset of the 49 cells (skips unanswered); aggregates are over what's present.
 */
export function buildLifeBalanceResult(cells: LbCellInput[]): LifeBalanceResult {
  const valid = cells.filter(c => Number.isFinite(c.answer));

  const matrix: LbCell[] = valid.map(c => {
    const answer = clampAnswer(c.answer);
    return {
      domain: c.domain,
      parameter: c.parameter,
      answer,
      deviation: deviation(answer),
      balance: balanceScore(answer),
      status: cellStatus(answer),
      potential: cellPotential(answer),
    };
  });

  const byKey = (key: 'domain' | 'parameter'): Record<string, LbAggregate> => {
    const groups: Record<string, number[]> = {};
    for (const cell of matrix) {
      (groups[cell[key]] ||= []).push(cell.answer);
    }
    const out: Record<string, LbAggregate> = {};
    for (const [name, answers] of Object.entries(groups)) out[name] = aggregate(answers);
    return out;
  };

  return {
    overall: aggregate(matrix.map(c => c.answer)),
    domains: byKey('domain'),
    parameters: byKey('parameter'),
    matrix,
  };
}

/** Extract a single domain's slice from a full result as its own 1-domain payload. */
export function sliceDomain(result: LifeBalanceResult, domain: string): LifeBalanceResult | null {
  const cells = result.matrix.filter(c => c.domain === domain).map(c => ({ domain: c.domain, parameter: c.parameter, answer: c.answer }));
  return cells.length ? buildLifeBalanceResult(cells) : null;
}

export interface LbDomainMeta {
  completedAt: string;
  status: LbStatus;
  balance: number;
}

export interface LifeBalanceOverview {
  /** Merged 7×7 from the latest take of each domain (null if nothing completed). */
  result: LifeBalanceResult | null;
  /** Per-domain freshness/score for the picker + progress chips. */
  perDomain: Record<string, LbDomainMeta>;
}

/**
 * Assemble the whole-life picture from per-domain attempts (the modular model):
 * for each domain, take its cells from the MOST RECENT attempt that covered it,
 * then rebuild the aggregates over the union. Domains never taken are simply absent.
 */
export function mergeLatestDomainResults(
  attempts: Array<{ completedAt: string; result: LifeBalanceResult | null | undefined }>,
): LifeBalanceOverview {
  const sorted = attempts
    .filter(a => a.result && a.completedAt)
    .slice()
    .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());

  const cells: LbCellInput[] = [];
  const perDomain: Record<string, LbDomainMeta> = {};

  for (const domain of LB_DOMAINS) {
    const found = sorted.find(a => a.result!.domains[domain]);
    if (!found) continue;
    const agg = found.result!.domains[domain];
    perDomain[domain] = { completedAt: found.completedAt, status: agg.status, balance: agg.balance };
    for (const c of found.result!.matrix) {
      if (c.domain === domain) cells.push({ domain: c.domain, parameter: c.parameter, answer: c.answer });
    }
  }

  return { result: cells.length ? buildLifeBalanceResult(cells) : null, perDomain };
}
